// MediaPipe Tasks Vision FaceDetector (BlazeFace short-range), self-hosted and lazily loaded
// after the first unlock so the kiosk's first paint never waits for it. Face PRESENCE only:
// boxes and confidences, no landmarks kept, no embeddings, no templates (plan D10).
//
// Size (tasks-vision 1.0.1): JS 155 KB (45 KB gzip) in its own chunk, the SIMD WASM 11.8 MB
// (3.4 MB gzip) plus a 323 KB loader, and the 230 KB model, all cached immutably after the
// first download. Only the SIMD build is served: every Android System WebView and iPadOS
// WebKit the kiosk supports has WASM SIMD, so the 11 MB non-SIMD twin is not shipped.
// Rejected: the Shape Detection API's FaceDetector (behind a flag on Android WebView, absent on
// iPad) and the legacy @mediapipe/face_detection solution (same WASM weight, unmaintained).
import type { Detection } from "./face-gate.ts";

export interface FaceDetectorLike { detect(video: HTMLVideoElement, now: number): Detection[]; close(): void }

export const MEDIAPIPE_BASE = "/mediapipe";
export const TASKS_VISION_VERSION = "1.0.1";

let loading: Promise<FaceDetectorLike | null> | null = null;

/** Loads once per page; resolves null (and the flow falls back to a manual photo) on failure. */
export function loadFaceDetector(base = MEDIAPIPE_BASE): Promise<FaceDetectorLike | null> {
  loading ??= (async () => {
    try {
      const { FaceDetector } = await import("@mediapipe/tasks-vision");
      const dir = `${base}/tasks-vision-${TASKS_VISION_VERSION}`;
      const detector = await FaceDetector.createFromOptions(
        { wasmLoaderPath: `${dir}/vision_wasm_internal.js`, wasmBinaryPath: `${dir}/vision_wasm_internal.wasm` },
        {
          baseOptions: { modelAssetPath: `${base}/blaze_face_short_range.tflite`, delegate: "CPU" },
          runningMode: "VIDEO", minDetectionConfidence: 0.5,
        },
      );
      let last = 0;
      return {
        detect(video, now) {
          // MediaPipe requires strictly increasing timestamps in VIDEO mode.
          const ts = Math.max(now, last + 1);
          last = ts;
          const r = detector.detectForVideo(video, ts);
          return r.detections.map((d) => ({
            conf: d.categories[0]?.score ?? 0,
            x: d.boundingBox?.originX ?? 0, y: d.boundingBox?.originY ?? 0,
            w: d.boundingBox?.width ?? 0, h: d.boundingBox?.height ?? 0,
          }));
        },
        close: () => detector.close(),
      } satisfies FaceDetectorLike;
    } catch (e) {
      console.warn("face detection unavailable", e);
      loading = null;
      return null;
    }
  })();
  return loading;
}
